import React, { Component } from 'react';
import withStyles from '@material-ui/styles/withStyles';
import PrintOptions from '../print-options/print-options';
import cypressTags from '../../support/cypressTags';
import { FaFileCsv } from 'react-icons/fa';

/** Sort option that CSV export does not support; button is disabled when this is selected */
export const CSV_UNSUPPORTED_SORT_BY = 'projecttitletvtalentstatusactivecastingontop';

export class PrintManager extends Component {
  constructor(props) {
    super(props);
    // Define defaults first, then merge with savedOptions to ensure all properties have values
    const defaults = {
      executives: true,
      includeLiteraryExecs: true,
      includeTalentExecs: true,
      staff: true,
      staffByPerson: false,
      includeStaffAgencies: false,
      paVocation: false,
      paVocationByPerson: false,
      includePAVocationAgencies: false,
      cast: true,
      index: true,
      talentNotes: false,
      literaryNotes: false,
      indieNotes: false,
      paNotes: false,
      includeInternalLiteraryNotes: false,
      includeDatesLiteraryNotes: false,
      budget: true,
      internalHeader: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      agents: true,
      talentAgents: true,
      literaryAgents: true,
      indieAgents: true,
      productionArtsAgents: true,
      budgetNotes: false,
      sortBy: '',
    };
    this.state = {
      ...defaults,
      ...(props.savedOptions || {}),
      actionError: null,
    };
  }

  print = async () => {
    try {
      // Handle both async and sync print functions
      // Promise.resolve() wraps non-promise return values, and awaits promises
      const result = this.props.print(this.state);
      await Promise.resolve(result);
      this.setState({ actionError: null });
      this.props.collapseEvent();
    } catch (error) {
      console.error('Error printing:', error);
      this.setState({
        actionError: 'Print failed. Please try again. If the problem persists, refresh the page and try once more.',
      });
    }
  };

  downloadCSV = async () => {
    try {
      // Handle both async and sync downloadCSV functions
      // Promise.resolve() wraps non-promise return values, and awaits promises
      const result = this.props.downloadCSV(this.state);
      await Promise.resolve(result);
      this.setState({ actionError: null });
      this.props.collapseEvent();
    } catch (error) {
      console.error('Error downloading CSV:', error);
      this.setState({
        actionError: 'CSV download failed. Please try again. If the problem persists, refresh the page and try once more.',
      });
    }
  };

  // eslint-disable-next-line no-undef
  onChange = selections => {
    this.setState(selections);
  };

  render() {
    const { actionError, sortBy } = this.state;
    const isCsvDisabled = sortBy === CSV_UNSUPPORTED_SORT_BY;
    const {
      collapseEvent,
      classes,
      indexOptionsDisabled,
      userInTalent,
      savedOptions,
      updateGrid,
      grid,
      canEdit,
      isCastingResults,
      docCount,
      showSortOptions,
      showCSVUi,
    } = this.props;
    return (
      <div className={classes.container}>
        <div className="ml-1 row">
          <h5 className="p-0 mx-0 mt-0 mb-2">Print</h5>
          <div style={{ cursor: 'pointer' }} className="ml-auto mr-3" onClick={collapseEvent}>
            <i className="fa fa-fw fa-window-close" aria-hidden="true" />
          </div>
        </div>

        <PrintOptions
          savedOptions={savedOptions}
          onSelectionsChanged={this.onChange}
          indexOptionsDisabled={indexOptionsDisabled}
          userInTalent={userInTalent}
          isCastingResults={isCastingResults}
          showSortOptions={showSortOptions}
          showCSVUi={showCSVUi}
        />
        {actionError ? (
          <p style={{ color: '#b00020', margin: '0 15px 10px 15px' }} role="alert">
            {actionError}
          </p>
        ) : null}
        {grid && canEdit && (
          <button
            className={'saveToGrid ' + classes.saveToGrid}
            onClick={async e => {
              e.preventDefault();
              grid.printOptions = this.state;
              await updateGrid(grid);
            }}
          >
            <i className={`fa fa-w fa-save ` + classes.icon} />
            Save print settings to grid
          </button>
        )}

        <div className={classes.buttonContainer}>
          <button onClick={collapseEvent} className={classes.button}>
            <i className={`fa fa-w fa-ban ${classes.icon}`} />
            Cancel
          </button>

          <button
            onClick={this.print.bind(this)}
            className={classes.button}
            data-cy={cypressTags.PROJECT_ACTIONS.PRINT_PROJECTS}
          >
            <i className={`fa fa-w fa-print ${classes.icon}`} />
            Print
          </button>
          {showCSVUi && (
            <button
              onClick={this.downloadCSV.bind(this)}
              className={classes.button}
              disabled={isCsvDisabled}
              title={isCsvDisabled ? 'CSV is not available for this sort order' : undefined}
              style={isCsvDisabled ? { cursor: 'not-allowed' } : undefined}
              data-cy={cypressTags.PROJECT_ACTIONS.DOWNLOAD_CSV}
            >
              <FaFileCsv />
              <span className="pl-1">CSV</span>
            </button>
          )}
        </div>
        {docCount && Number(docCount) > 5000 ? (
          <p>
            <span>&#9888; </span>
            please print documents less than or equal to 5000, get a more accurate pdf.
          </p>
        ) : (
          ''
        )}
      </div>
    );
  }
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F5F5F5',
    right: 0,
    marginTop: '-19px',
    minWidth: '250px',
    width: '25%',
    borderLeft: '1px solid #dcdcdc',
    borderBottom: '1px solid #dcdcdc',
    position: 'absolute',
    padding: '15px 15px 0 15px',
    top: 'auto',
    minHeight: '100%',
    zIndex: 1,
  },
  icon: {
    marginRight: 5,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    margin: 15,
  },
  button: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    border: 'none',
    fontSize: 14,
    lineHeight: 1,
    backgroundColor: 'unset',
    textTransform: 'uppercase',
  },
  saveToGrid: {
    backgroundColor: 'unset',
    textTransform: 'uppercase',
    border: 'none',
    fontSize: 14,
    margin: 15,
  },
};

export default withStyles(styles)(PrintManager);
